import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { ConfigRow } from "@/features/admin/config-row";
import { Empty, PageHeader, Rows } from "@/features/admin/parts";
import { type AdminConfigFilter, allConfigsQuery } from "@/lib/api/queries";
import { cn } from "@/lib/cn";

const FILTERS = [
  { id: "all", label: "Все", filter: {} },
  { id: "unowned", label: "Без владельца", filter: { unowned: true } },
  { id: "active", label: "Активные", filter: { status: "active" } },
  { id: "suspended", label: "Приостановленные", filter: { status: "suspended" } },
  {
    id: "revoked_externally",
    label: "Удалены в приложении",
    filter: { status: "revoked_externally" },
  },
  { id: "revoked", label: "Отозванные", filter: { status: "revoked" } },
] as const satisfies readonly { id: string; label: string; filter: AdminConfigFilter }[];

type FilterId = (typeof FILTERS)[number]["id"];

export const Route = createFileRoute("/_app/admin/configs")({
  validateSearch: z.object({
    filter: z.enum(FILTERS.map((f) => f.id) as [FilterId, ...FilterId[]]).optional(),
  }),
  component: ConfigsPage,
});

function ConfigsPage() {
  const { filter: filterId = "all" } = Route.useSearch();
  const current = FILTERS.find((f) => f.id === filterId) ?? FILTERS[0];
  const configs = useQuery({
    ...allConfigsQuery(current.filter),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Конфиги" />
      <p className="mt-2 max-w-prose text-sm text-muted">
        Все пиры сервера, включая созданные в приложении Amnezia. У таких нет ключа в портале —
        скачать их нельзя, но можно привязать к пользователю или отозвать.
      </p>
      <nav
        aria-label="Фильтр"
        className="-mx-4 mt-5 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none]"
      >
        {FILTERS.map((f) => (
          <Link
            key={f.id}
            from={Route.fullPath}
            search={f.id === "all" ? {} : { filter: f.id }}
            replace
            className={cn(
              "h-9 shrink-0 rounded-full border px-4 text-sm leading-[2.125rem]",
              f.id === filterId
                ? "border-amber bg-amber/10"
                : "border-line text-muted hover:text-text",
            )}
          >
            {f.label}
          </Link>
        ))}
      </nav>
      <div className="mt-4">
        {configs.data?.length === 0 ? (
          <Empty>Здесь пусто.</Empty>
        ) : (
          <Rows>
            {configs.data?.map((config) => (
              <ConfigRow
                key={config.id}
                config={config}
                owner={{ id: config.user_id, username: config.owner_username }}
              />
            ))}
          </Rows>
        )}
      </div>
    </>
  );
}
